import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import ActionsContainer from 'screens/admin/Reviews/ReviewProcess/ReviewCycle/actionsContainer';
import styled, { css } from 'styled-components';
import { StyledTextField, StyledTypography } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import theme from 'constants/theme';

import { getSurvey, createSurvey, updateSurveyKickStart } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import { DeerFooterContainer } from './DeerFooterContainer';
import DriversSelector from './DriversSelector';

const SurveyName = ({ formId, backFn, nextFn, updateFormId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [surveyName, setSurveyName] = useState();
  const [selectedDriversIds, setSelectedDriversIds] = useState([]);
  const isNewSurvey = Number.isNaN(Number(formId));
  const getSurveyData = () => {
    setLoading(true);
    getSurvey({ surveyId: formId })
      .then((res) => {
        if (res) {
          setSurveyName(res.name);
          setSelectedDriversIds(res.surveyDrivers.map((item) => item.uuid));
        } else {
          dispatch(showErrorSnackbar('Sorry, unable to fetch the survey details!'));
          backFn();
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to fetch the survey details!'));
        backFn();
      })
      .finally(() => setLoading(false));
  };

  const updateSurvey = () => {
    setLoading(true);
    updateSurveyKickStart({
      body: { id: formId, name: surveyName, surveyDrivers: selectedDriversIds },
    })
      .then((res) => {
        nextFn();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to save new survey data!'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isNewSurvey) {
      getSurveyData();
    } else {
      setLoading(false);
    }
  }, [formId]);

  const handleNameChange = (e) => setSurveyName(e.target.value);

  const save = () => {
    setLoading(true);
    createSurvey({ body: { name: surveyName, surveyDrivers: selectedDriversIds } })
      .then((res) => {
        if (res && res.id) {
          updateFormId(res.id);
          nextFn();
        } else {
          dispatch(showErrorSnackbar('Sorry, unable to create a new survey!'));
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to save the new survey!'));
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
        Name or select your survey
      </StyledTypography>
      <StyledTypography variant="body" customColor={theme.palette.text.label}>
        You can create a new survey or instance of
        <br />
        existing survey with different drivers
      </StyledTypography>
      <FormContainer>
        <StyledTypography customColor={theme.palette.text.heading}>Engagement name</StyledTypography>
        <StyledTextField
          onChange={handleNameChange}
          placeholder="Eg. 2021 Jan engagement"
          isDisabled={loading || !isNewSurvey}
          variant="outlined"
          fullWidth
          margin="dense"
          value={surveyName}
        />

        <StyledTypography customColor={theme.palette.text.heading} topGutter={3} bottomGutter={1}>
          Drivers
        </StyledTypography>
        {isNewSurvey ? (
          <DriversSelector onChange={setSelectedDriversIds} />
        ) : (
          !loading && <DriversSelector prevDrivers={selectedDriversIds} onChange={setSelectedDriversIds} />
        )}
        <StyledTypography customColor={theme.palette.text.heading} topGutter={3}>
          Survey type
        </StyledTypography>
        <Grid container>
          <Grid item xs={6}>
            <ReviewTypeContainer active>
              <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
                Org chart survey (default)
              </StyledTypography>
              <StyledTypography variant="body" customColor={theme.palette.text.heading}>
                Based off traditional manager relationships and hierarchical structures
              </StyledTypography>
            </ReviewTypeContainer>
          </Grid>
          <Grid item xs={6}>
            <ReviewTypeContainer disabled>
              <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
                Event based survey
              </StyledTypography>
              <StyledTypography variant="body" customColor={theme.palette.text.heading} topGutter={3}>
                Based on employee timeline, achievements & other important events
              </StyledTypography>
            </ReviewTypeContainer>
          </Grid>
        </Grid>
      </FormContainer>
      <ActionsContainer
        ContainerComp={checkNewNavEnabled() ? DeerFooterContainer : null}
        loading={loading}
        onBackClick={backFn}
        onNextClick={isNewSurvey ? save : updateSurvey}
        backText="cancel"
        nextDisabled={!surveyName || selectedDriversIds.length === 0}
      />
    </>
  );
};

const FormContainer = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  margin-top: 1.5rem;
  margin-bottom: 5rem;
  padding: 1rem 1.5rem 2.5rem 1.5rem;
`;

const ReviewTypeContainer = styled.div`
  width: 90%;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 1rem 5.5rem 1.25rem 1.35rem;
  margin-top: 1%;
  cursor: pointer;
  height: 100%;
  ${(props) =>
    props.active &&
    css`
      background: #e6f6fd;
      border-color: #038eef;
      .MuiTypography-root {
        color: #038eef;
      }
    `};
  }
  ${(props) =>
    props.disabled &&
    css`
      cursor: initial;
      opacity: 0.5;
      pointer-events: none;
    `};
  }
`;

export default SurveyName;
